import { inject } from '@angular/core';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import { Injectable } from '@banta/common';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { ApiMiddlewareService, ApiRequest } from '@tytapp/app/middleware/api-middleware.service';
import { buildQuery, LoggerService } from '@tytapp/common';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';

import type * as express from '../../common/express-ssr';

interface SavedApiCall {
    path: string;
    response: any;
    count: number;
}

const API_CALL_TRANSFER = makeStateKey<SavedApiCall[]>('apiCallTransfer');

/**
 * Transparent caching of API calls made in SSR for replay on CSR using Angular's
 * TransferState service.
 */
@Injectable()
export class ApiStateTransfer {
    private request = inject<express.ExpressRequest>(REQUEST, { optional: true });
    private transferState = inject(TransferState);
    private apiMiddleware = inject(ApiMiddlewareService);
    private logger = inject(LoggerService);
    private apiCalls: SavedApiCall[] = [];

    private replay(request: ApiRequest, consume = true) {
        if (request.method !== 'GET')
            return undefined;

        let fullPath = `${request.path}?${buildQuery(request.query)}`;

        let matchingCallIndex = this.apiCalls.findIndex(x => x.path === fullPath);
        if (matchingCallIndex >= 0) {
            let matchingCall = this.apiCalls[matchingCallIndex];
            if (consume) {
                matchingCall.count -= 1;
                if (matchingCall.count <= 0) {
                    this.apiCalls.splice(matchingCallIndex, 1);
                }
            } else {
                this.logger.warning(`[ApiStateTransfer] Duplicate API call ${matchingCall.path} [${matchingCall.count + 1} uses so far]- this is inefficient.`);
                matchingCall.count += 1;
                this.transferState.set(API_CALL_TRANSFER, this.apiCalls);
            }

            if (!this.request)
                this.logger.info(`[ApiStateTransfer] Replayed API call ${matchingCall.path} [${matchingCall.count} uses left]`);

            return of(matchingCall.response);
        }
    }

    /**
     * Store an API request/response pair for sending to clients for replay. Normally this
     * is handled transparently. In cases where API calls are cached between SSR requests,
     * it may be desirable to inject those cached API calls so that they are not replayed
     * on the client.
     *
     * @param method
     * @param path
     * @param query
     * @param response
     * @returns
     */
    store(request: ApiRequest, response: any) {
        if (!this.request || request.method !== 'GET')
            return;

        this.apiCalls.push({ path: `${request.path}?${buildQuery(request.query)}`, response, count: 1 });
        this.transferState.set(API_CALL_TRANSFER, this.apiCalls);
    }

    prepopulate(path: string, query: Record<string, string>, value: any) {
        this.store({ method: 'GET', headers: {}, body: undefined, path, query }, value);
    }

    private installCapturer() {
        this.transferState.set(API_CALL_TRANSFER, this.apiCalls);
        this.apiMiddleware.install((req, next) => this.replay(req, false)
            ?? next(req).pipe(map(res => (this.store(req, res), res))));
    }

    private installReplayer() {
        this.apiCalls = this.transferState.get(API_CALL_TRANSFER, []);
        this.apiMiddleware.install((request, next) => this.replay(request) ?? next(request));

        // Logging

        if (this.apiCalls.length > 0) {
            this.logger.info(`[ApiStateTransfer] Ready to replay ${this.apiCalls.length} stored API calls.`);
            this.logger.inspect(this.apiCalls.slice());
        }

        setTimeout(() => {
            if (this.apiCalls.length > 0) {
                this.logger.info(`[ApiStateTransfer] ${this.apiCalls.length} API calls were not consumed:`);
                this.logger.inspect(this.apiCalls);
            }

            this.apiCalls = [];
        }, 10_000);
    }

    install() {
        if (this.request)
            this.installCapturer();
        else
            this.installReplayer();
    }
}